import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Instagram, Building2, UtensilsCrossed, MessageSquare, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { SOCIALS } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOUCH_ICONS = [Building2, UtensilsCrossed, MessageSquare];

const Reveal = ({ children, delay = 0, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

const inputCls =
  "w-full rounded-xl border border-black/10 bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink/40 outline-none transition-colors focus:border-brand [color-scheme:light]";

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink/50">{label}</span>
    {children}
  </label>
);

export default function ContactPage({ t }) {
  const c = t.contactPage;
  const f = c.form;
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", event_date: "", guests: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/catering`, {
        ...form,
        guests: form.guests ? Number(form.guests) : null,
        company: form.company || null,
        phone: form.phone || null,
        event_date: form.event_date || null,
        budget: form.budget || null,
      });
      toast.success(f.success);
      setForm({ name: "", email: "", company: "", phone: "", event_date: "", guests: "", budget: "", message: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const infoCards = [
    { icon: MapPin, title: c.visit, value: t.footer.address, testId: "contact-visit" },
    { icon: Mail, title: c.emailUs, value: t.footer.email, href: `mailto:${t.footer.email}`, testId: "contact-email" },
    { icon: Phone, title: c.callUs, value: t.footer.phone, href: `tel:${t.footer.phone.replace(/\s/g, "")}`, testId: "contact-phone" },
    { icon: Instagram, title: c.followUs, value: SOCIALS.igHandle, href: SOCIALS.instagram, testId: "contact-instagram" },
  ];

  return (
    <main data-testid="contact-page">
      <Seo title="Contact — Kurkuma AI Kitchen" siteName="Kurkuma AI Kitchen" description={c.heroSub} />

      <section className="bg-navy py-16 text-cream md:py-20" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl" data-testid="contact-heading">{c.heroTitle}</h1>
            <p className="mt-2 text-cream/70">{c.heroSub}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* info */}
          <div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infoCards.map((card, i) => (
                <Reveal key={card.title} delay={i * 0.06}>
                  <a
                    href={card.href || "#contact-form"}
                    data-testid={card.testId}
                    className="block h-full rounded-2xl bg-paper p-6 shadow-sm transition-shadow hover:shadow-lg"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/15 text-branddark">
                      <card.icon className="h-5 w-5" />
                    </span>
                    <p className="mt-3 font-display text-sm font-semibold text-navy">{card.title}</p>
                    <p className="mt-1 text-sm text-ink/55">{card.value}</p>
                  </a>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-10" delay={0.1}>
              <h2 className="mb-4 font-display text-lg font-bold text-navy">{c.touchTitle}</h2>
              <div className="flex flex-wrap gap-3">
                {c.touch.map((item, i) => {
                  const Icon = TOUCH_ICONS[i];
                  return (
                    <span key={item} className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-paper px-4 py-2 text-sm font-medium text-ink/70">
                      <Icon className="h-4 w-4 text-branddark" /> {item}
                    </span>
                  );
                })}
              </div>
            </Reveal>

            <Reveal className="mt-10" delay={0.15}>
              <div className="overflow-hidden rounded-3xl shadow-md" data-testid="contact-map">
                <iframe
                  title="Kurkuma AI Kitchen — Warsaw map"
                  src="https://maps.google.com/maps?q=Posing%207%2F11%2C%2000-124%20Warszawa&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                />
              </div>
              <p className="mt-2 flex items-center gap-2 text-xs text-ink/50">
                <MapPin className="h-3.5 w-3.5 text-branddark" /> {c.mapTitle}
              </p>
            </Reveal>
          </div>

          {/* form */}
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} id="contact-form" className="rounded-3xl bg-paper p-8 shadow-md" data-testid="contact-form-card">
              <h2 className="mb-6 font-display text-xl font-bold text-navy">{f.title}</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label={f.name} testId="contact-field-name">
                  <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-input-name" />
                </Field>
                <Field label={f.email} testId="contact-field-email">
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-input-email" />
                </Field>
                <Field label={f.company} testId="contact-field-company">
                  <input value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-input-company" />
                </Field>
                <Field label={f.phone} testId="contact-field-phone">
                  <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-input-phone" />
                </Field>
                <Field label={f.date} testId="contact-field-date">
                  <input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="contact-input-date" />
                </Field>
                <Field label={f.guests} testId="contact-field-guests">
                  <input type="number" min="1" value={form.guests} onChange={set("guests")} className={inputCls} data-testid="contact-input-guests" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label={f.budget} testId="contact-field-budget">
                    <select value={form.budget} onChange={set("budget")} className={inputCls} data-testid="contact-input-budget">
                      <option value="">—</option>
                      {f.budgets.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={f.message} testId="contact-field-message">
                    <textarea required rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="contact-input-message" />
                  </Field>
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3 text-sm font-semibold text-navy transition-colors hover:bg-branddark disabled:opacity-60"
              >
                {sending ? f.sending : f.send}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
